import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { 
  Loader2, TrendingUp, TrendingDown, Minus, Clock, 
  ArrowRight, BarChart3, Flame, FileText
} from "lucide-react";
import { toast } from "sonner";
import { ReadinessBadgeInline } from "../components/ReadinessBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScoreHistoryPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login?redirect=/score-history");
      return;
    }
    fetchHistory();
  }, [user]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/score-history`, {
        headers: getAuthHeaders()
      });
      setHistory(response.data);
    } catch (error) {
      toast.error("Failed to load score history");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const quickTests = history?.quick_tests || { history: [], count: 0 };
  const fullAssessments = history?.full_assessments || { history: [], count: 0 };
  const hasHistory = quickTests.count > 0 || fullAssessments.count > 0;

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Score History</h1>
          <p className="text-muted-foreground">
            Track your progress over time across Quick Tests and Full Assessments
          </p>
        </div>

        {!hasHistory ? (
          <Card className="text-center py-12">
            <CardContent>
              <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">No scores yet</h2>
              <p className="text-muted-foreground mb-6">
                Complete a quiz or assessment to start tracking your progress
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/quiz">
                  <Button variant="outline" className="rounded-full">
                    Take Quick Test
                  </Button>
                </Link>
                <Link to="/assessment">
                  <Button className="rounded-full">
                    Take Full Assessment
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Test History */}
            <Card data-testid="quick-test-history">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Flame className="w-5 h-5 text-primary" />
                  Quick Test History
                </CardTitle>
                <CardDescription>
                  {quickTests.count} test{quickTests.count !== 1 ? "s" : ""} completed
                </CardDescription>
              </CardHeader>
              <CardContent>
                {quickTests.improvement_message && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">{quickTests.improvement_message}</span>
                    </div>
                  </div>
                )}

                {quickTests.history.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      No Quick Tests completed yet
                    </p>
                    <Link to="/quiz">
                      <Button variant="outline" size="sm" className="rounded-full">
                        Take Quick Test
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {quickTests.history.slice(0, 5).map((test, index) => (
                      <Link 
                        key={test.id} 
                        to={`/quiz/results/${test.id}`}
                        className="block"
                      >
                        <div className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <span className="text-lg font-bold">{test.percentage}%</span>
                            </div>
                            <div>
                              <ReadinessBadgeInline level={test.readiness_level} />
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(test.created_at)}
                              </p>
                            </div>
                          </div>
                          {index === 0 && quickTests.improvement !== null && quickTests.improvement !== 0 && (
                            <div className={`flex items-center gap-1 text-sm ${
                              quickTests.improvement > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {quickTests.improvement > 0 ? (
                                <TrendingUp className="w-4 h-4" />
                              ) : (
                                <TrendingDown className="w-4 h-4" />
                              )}
                              <span>{Math.abs(quickTests.improvement)}</span>
                            </div>
                          )}
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {quickTests.history.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <Link to="/quiz">
                      <Button variant="outline" size="sm" className="w-full rounded-full">
                        Take Another Quick Test
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Full Assessment History */}
            <Card data-testid="full-assessment-history">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-secondary" />
                  Full Assessment History
                </CardTitle>
                <CardDescription>
                  {fullAssessments.count} assessment{fullAssessments.count !== 1 ? "s" : ""} completed
                </CardDescription>
              </CardHeader>
              <CardContent>
                {fullAssessments.improvement_message && (
                  <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">{fullAssessments.improvement_message}</span>
                    </div>
                  </div>
                )}

                {fullAssessments.history.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-muted-foreground mb-4">
                      No Full Assessments completed yet
                    </p>
                    <Link to="/assessment">
                      <Button variant="outline" size="sm" className="rounded-full">
                        Take Full Assessment
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {fullAssessments.history.slice(0, 5).map((assessment, index) => (
                      <Link 
                        key={assessment.id} 
                        to={`/assessment/results/${assessment.id}`}
                        className="block"
                      >
                        <div className="flex items-center justify-between p-3 rounded-lg border hover:border-secondary/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="text-center">
                              <span className="text-lg font-bold">{assessment.total_score}</span>
                              <span className="text-xs text-muted-foreground">/100</span>
                            </div>
                            <div>
                              <ReadinessBadgeInline level={assessment.readiness_level} />
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(assessment.created_at)}
                              </p>
                            </div>
                          </div>
                          {index === 0 && fullAssessments.improvement !== null && fullAssessments.improvement !== 0 && (
                            <div className={`flex items-center gap-1 text-sm ${
                              fullAssessments.improvement > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {fullAssessments.improvement > 0 ? (
                                <TrendingUp className="w-4 h-4" />
                              ) : (
                                <TrendingDown className="w-4 h-4" />
                              )}
                              <span>{Math.abs(fullAssessments.improvement)}</span>
                            </div>
                          )}
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {fullAssessments.history.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <Link to="/assessment">
                      <Button variant="outline" size="sm" className="w-full rounded-full">
                        Take Another Assessment
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info section */}
        <Card className="mt-8">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-3">How Score Tracking Works</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">1</span>
                </div>
                <div>
                  <p className="font-medium">Take assessments regularly</p>
                  <p className="text-muted-foreground text-xs">
                    We recommend retaking every 30 days
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium">Work on weak areas</p>
                  <p className="text-muted-foreground text-xs">
                    Focus on the sections highlighted for improvement
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium">Track your progress</p>
                  <p className="text-muted-foreground text-xs">
                    See your scores improve as you validate your idea
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
